<script setup>
import { ref, watch, onBeforeMount, nextTick } from "vue";
import { useCompanyStore } from "@/stores/company";
import { useUiStore } from "@/stores/ui";
import { useDataCollectionStore } from "@/stores/dataCollection";
import { useRouter, useRoute } from "vue-router";
import { useToast } from "vue-toastification";
import axios from "axios";

const toast = useToast();
const router = useRouter();
const route = useRoute();
const uiStore = useUiStore();
const dataCollectionStore = useDataCollectionStore();
import AgentLayout from "@/views/layouts/AgentLayout.vue";
import PageHeader from "@/components/shared/PageHeader.vue";
import DBreadcrumb from "@/components/shared/DBreadcrumb.vue";
const companyId = useCompanyStore().companyId;

const currentStep = ref(1);
const id = ref(null);
const update = ref(false);
const breadcrumbItems = ref([]);
const errors = ref({});
const mappedFiles = ref([]);
const isSubmitted = ref(false);

const multipleFileInput = ref(null);

const triggerAddFiles = () => {
  if (multipleFileInput.value) multipleFileInput.value.click();
};

const handleMultipleFileUpload = (event) => {
  const files = Array.from(event.target.files);
  files.forEach((file) => {
    mappedFiles.value.push({
      name: file.name,
      file: file,
      originalName: file.name,
      size: (file.size / (1024 * 1024)).toFixed(1) + " MB",
      status: "Ready",
      file_type: file.type,
      updated: true,
      uploadPercentage: 0,
    });
  });
  // Reset input value to allow selecting same files again if needed
  if (multipleFileInput.value) multipleFileInput.value.value = "";
};

const removeFile = (index) => {
  mappedFiles.value.splice(index, 1);
};

const handleFileUpload = (event, index) => {
  const file = event.target.files[0];
  if (file && mappedFiles.value[index]) {
    mappedFiles.value[index].file = file;
    mappedFiles.value[index].originalName = file.name;
    mappedFiles.value[index].size =
      (file.size / (1024 * 1024)).toFixed(1) + " MB";
    mappedFiles.value[index].status = "Ready";
    mappedFiles.value[index].updated = update.value ? true : false;

    if (!mappedFiles.value[index].name) {
      mappedFiles.value[index].name = file.name;
    }
  }
};

const triggerFileUpload = (index) => {
  const input = document.getElementById(`file-input-${index}`);
  if (input) input.click();
};

const validate = () => {
  errors.value = {};
  let isValid = true;

  const hasContent = mappedFiles.value.length > 0;

  if (!hasContent) {
    errors.value.content =
      "At least one file is required to create a data source.";
    isValid = false;
  }

  const invalidFileIndex = mappedFiles.value.findIndex(
    (f) => !f.name || !f.name.trim(),
  );
  if (invalidFileIndex !== -1) {
    errors.value.fileIndex = invalidFileIndex;
    isValid = false;
  }

  return isValid;
};

watch(
  [mappedFiles],
  () => {
    const hasContent = mappedFiles.value.length > 0;
    if (hasContent) {
      delete errors.value.content;
    }
  },
  { deep: true },
);

const goToDataLibrary = () => {
  router.push("/data-library");
};

const createFormData = () => {
  const formData = new FormData();
  formData.append("company_id", companyId);
  formData.append("parent_id", route.query.id || "");
  mappedFiles.value.forEach((file, index) => {
    formData.append(`files[${index}][name]`, file.name);
    if (file.file) {
      formData.append(`files[${index}][file]`, file.file);
    }
    formData.append(`files[${index}][updated]`, file.updated);
    formData.append(`files[${index}][originalName]`, file.originalName || "");
    formData.append(`files[${index}][size]`, file.size || "");
    formData.append(`files[${index}][status]`, file.status || "");
    formData.append(`files[${index}][file_type]`, file.file_type || "");
  });

  return formData;
};

const sendDataSource = (formData) => {
  axios
    .post(
      `${process.env.VUE_APP_BASE_URL}/api/files-upload-multiple`,
      formData,
      {
        headers: {
          Accept: "application/json",
          "Content-type": "multipart/form-data",
        },
        withCredentials: true,
        onUploadProgress: (progressEvent) => {
          const progressRatio = progressEvent.loaded / progressEvent.total;
          const filesToUpload = mappedFiles.value.filter((f) => f.file);
          const totalSize = filesToUpload.reduce(
            (acc, f) => acc + f.file.size,
            0,
          );
          let loadedDist = progressRatio * totalSize;

          filesToUpload.forEach((file) => {
            if (loadedDist >= file.file.size) {
              file.uploadPercentage = 100;
              loadedDist -= file.file.size;
            } else if (loadedDist > 0) {
              file.uploadPercentage = Math.floor(
                (loadedDist / file.file.size) * 100,
              );
              loadedDist = 0;
            } else {
              file.uploadPercentage = 0;
            }
          });
        },
      },
    )
    .then((res) => {
      const summary = res.data.data;
      const duplicateFiles = summary?.files?.filter(
        (f) => f.status === "duplicate",
      );

      if (duplicateFiles?.length) {
        const names = duplicateFiles.map((f) => f.name).join(", ");
        toast.warning(`Already exists in this location: ${names}`);
      }

      if (summary?.uploaded || summary?.deduplicated) {
        toast.success(res.data.message);
      }

      goToDataLibrary();
    })
    .catch((e) => {
      toast.error(e.response.data.message);
      isSubmitted.value = false;
    });
};

const onSubmit = () => {
  if (!validate()) {
    if (errors.value.fileIndex !== undefined) {
      nextTick(() => {
        const el = document.getElementById(
          `file-name-${errors.value.fileIndex}`,
        );
        if (el) {
          el.focus();
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    }
    return;
  }
  isSubmitted.value = true;

  if (mappedFiles.value.some((f) => f.file)) {
    currentStep.value = 2;
  }

  const formData = createFormData();
  sendDataSource(formData);
};

const cancel = () => {
  goToDataLibrary();
};

const fetchDataSourceById = async (id) => {
  dataCollectionStore
    .fetchDataSourceById({
      companyId,
      id,
    })
    .then((res) => {
      update.value = true;
      mappedFiles.value = res.data.files;
      mappedFiles.value.forEach((file) => {
        file.file = null;
        file.updated = false;
      });
      breadcrumbItems.value = [
        { text: "Data Library", to: { name: "DataLibraryView" } },
        { text: res.data.name, active: true },
      ];
    })
    .catch(() => {
    });
};
const resetStoreData = () => {
  uiStore.resetModalStore();
};

onBeforeMount(async () => {
  if (route.params.id) {
    id.value = route.params.id;
    await fetchDataSourceById(route.params.id);
  } else {
    resetStoreData();
  }
});
</script>

<template>
  <AgentLayout>
    <div class="upload-page-container">
      <DBreadcrumb v-if="update" :items="breadcrumbItems" />
      <PageHeader
        :title="update ? 'Edit Data Source' : 'Upload Files'"
        :subtitle="
          update
            ? 'Update your file details'
            : 'Upload new documents to your knowledge base'
        "
        :showButton="false"
      />

      <div class="upload-content-wrapper">
        <div class="upload-card">
          <!-- Empty State / Main Upload Zone -->
          <div
            v-if="mappedFiles.length === 0"
            class="upload-zone-large"
            @click="triggerAddFiles"
            :class="{ 'has-error': errors.content }"
          >
            <div class="upload-icon-circle">
              <i class="dx-icon-upload"></i>
            </div>
            <h3>Upload Documents</h3>
            <p>Drag & drop your files here or click to browse</p>
            <span class="supported-formats">Supported formats: .pdf, .csv</span>
            <p v-if="errors.content" class="error-msg-inline mt-2">
              {{ errors.content }}
            </p>
          </div>

          <!-- File List -->
          <div v-else class="file-list-container">
            <div class="file-list-header">
              <h3>Files ({{ mappedFiles.length }})</h3>
              <button class="btn-text-primary" @click="triggerAddFiles">
                <i class="dx-icon-add"></i> Add More Files
              </button>
            </div>

            <div class="file-cards-grid">
              <transition-group name="list">
                <div
                  v-for="(item, index) in mappedFiles"
                  :key="index"
                  class="file-card"
                >
                  <div class="file-card-icon">
                    <i class="bi bi-file-earmark-text"></i>
                  </div>

                  <div class="file-card-content">
                    <div class="form-group mb-2">
                      <label>File Name</label>
                      <input
                        :id="`file-name-${index}`"
                        v-model="item.name"
                        type="text"
                        class="clean-input"
                        :class="{ 'border-danger': !item.name }"
                        placeholder="Enter file name"
                      />
                      <span
                        v-if="!item.name"
                        class="text-xs text-danger mt-1 block"
                        >File name is required</span
                      >
                    </div>

                    <div class="file-meta">
                      <span class="badge-type">{{
                        item.file_type || "Unknown"
                      }}</span>
                      <span class="text-size">{{ item.size }}</span>

                      <div v-if="item.file_details" class="original-name">
                        Original:
                        {{
                          item.file_details.original_name ||
                          item.file_details.s3_name
                        }}
                      </div>
                    </div>

                    <!-- Re-upload Zone for Single File -->
                    <div
                      class="mini-reupload"
                      @click="triggerFileUpload(index)"
                      v-if="!item.file && !item.file_details"
                    >
                      <i class="dx-icon-upload"></i> Upload File content
                      <input
                        type="file"
                        :id="`file-input-${index}`"
                        class="hidden-input"
                        accept=".pdf,.csv"
                        @change="(e) => handleFileUpload(e, index)"
                      />
                    </div>

                    <div
                      v-if="item.file_details && item.file_details.url"
                      class="mt-2"
                    >
                      <a
                        :href="item.file_details.url"
                        target="_blank"
                        class="link-primary text-sm"
                      >
                        View File <i class="bi bi-box-arrow-up-right"></i>
                      </a>
                    </div>

                    <!-- Progress Bar -->
                    <div
                      v-if="isSubmitted && !item.file_details"
                      class="upload-progress mt-2"
                    >
                      <div class="flex justify-between text-xs mb-1">
                        <span>Uploading...</span>
                        <span>{{ item.uploadPercentage }}%</span>
                      </div>
                      <div class="custom-progress w-full bg-gray-200 rounded-full" style="height: 0.5rem">
                        <div class="bg-blue-500 rounded-full transition-all" style="height: 0.5rem"
                             :style="{ width: item.uploadPercentage + '%' }"></div>
                      </div>
                    </div>
                  </div>

                  <div class="file-card-actions">
                    <button
                      :disabled="isSubmitted"
                      class="btn-icon-danger"
                      @click="removeFile(index)"
                      title="Remove File"
                    >
                      <i class="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              </transition-group>
            </div>
          </div>

          <!-- Hidden Input for Multiple Files -->
          <input
            type="file"
            ref="multipleFileInput"
            class="hidden-input"
            multiple
            accept=".pdf,.csv"
            @change="handleMultipleFileUpload"
            style="display: none"
          />
        </div>
      </div>

      <!-- Sticky Footer -->
      <div class="sticky-footer">
        <div class="footer-content">
          <button class="btn-secondary" @click="cancel" :disabled="isSubmitted">
            Cancel
          </button>
          <button class="btn-primary" @click="onSubmit" :disabled="isSubmitted">
            <span
              v-if="isSubmitted"
              class="spinner-sm mr-2"
              role="status"
              aria-hidden="true"
            ></span>
            {{ update ? "Save Changes" : "Upload Files" }}
          </button>
        </div>
      </div>
    </div>
  </AgentLayout>
</template>

<style lang="scss" scoped>
.upload-page-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
}

.upload-content-wrapper {
  flex: 1;
  margin: 0 auto;
  width: 100%;
  overflow-y: auto;
}

.upload-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05),
    0 2px 4px -1px rgba(0, 0, 0, 0.03);
  padding: 2rem;
  min-height: 400px;
}

/* Upload Zone */
.upload-zone-large {
  border: 2px dashed #e2e8f0;
  border-radius: 12px;
  padding: 4rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 100%;
  min-height: 350px;
  background-color: #f8fafc;

  &:hover {
    border-color: #f8fbff;
    background-color: #eff6ff;

    .upload-icon-circle {
      transform: translateY(-5px);
      background-color: white;
      color: #1e293b;
    }
  }

  &.has-error {
    border-color: #ef4444;
    background-color: #fef2f2;

    .error-msg-inline {
      color: #ef4444;
      font-weight: 500;
    }
  }

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    margin: 1rem 0 0.5rem;
    color: #1e293b;
  }

  p {
    color: #64748b;
    margin-bottom: 0.5rem;
  }

  .supported-formats {
    font-size: 0.875rem;
    color: #94a3b8;
    background: #e2e8f0;
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
  }
}

.upload-icon-circle {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: #1e293b;
  transition: all 0.3s ease;
  margin-bottom: 1rem;
}

/* File List */
.file-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e2e8f0;

  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
  }
}

.file-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
}

.file-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.25rem;
  display: flex;
  gap: 1rem;
  position: relative;
  transition: all 0.2s;

  &:hover {
    border-color: #1e293b;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05);
    transform: translateY(-2px);
  }
}

.file-card-icon {
  width: 48px;
  height: 48px;
  background-color: #f1f5f9;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #1e293b;
  flex-shrink: 0;
}

.file-card-content {
  flex: 1;
  min-width: 0; /* For truncation */
}

.clean-input {
  width: 100%;
  border: none;
  border-bottom: 1px solid #cbd5e1;
  padding: 0.25rem 0;
  font-size: 0.95rem;
  font-weight: 500;
  color: #1e293b;
  background: transparent;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #1e293b;
  }

  &::placeholder {
    color: #cbd5e1;
    font-weight: 400;
  }
}

.border-danger {
  border-color: #ef4444 !important;
}

.text-danger {
  color: #ef4444;
}

.file-meta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.5rem;
  font-size: 0.8rem;
}

.badge-type {
  background-color: #eff6ff;
  color: #1e293b;
  padding: 2px 8px;
  border-radius: 6px;
  font-weight: 600;
  text-transform: uppercase;
}

.text-size {
  color: #64748b;
}

.original-name {
  font-size: 0.75rem;
  color: #94a3b8;
  margin-top: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mini-reupload {
  margin-top: 0.75rem;
  font-size: 0.85rem;
  color: #64748b;
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
  padding: 0.5rem;
  text-align: center;
  cursor: pointer;
  background: #f8fafc;
  transition: all 0.2s;

  &:hover {
    background: #f1f5f9;
    color: var(--primary-color);
    border-color: var(--primary-color);
  }
}

.file-card-actions {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.btn-icon-danger {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 0.25rem;
  transition: color 0.2s;

  &:hover:not(:disabled) {
    color: #ef4444;
  }
}

.btn-text-primary {
  background: none;
  border: none;
  color: var(--primary-color);
  font-weight: 600;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  transition: background 0.2s;

  &:hover {
    background-color: #eff6ff;
  }
}

/* Footer & Buttons */
.sticky-footer {
  width: 100%;
  position: fixed;
  bottom: 0;
  right: 0;
  background: white;
  border-top: 1px solid #e2e8f0;
  padding: 1.5rem;
  display: flex;
  justify-content: flex-end;
  z-index: 100;
}

.footer-content {
  width: 100%;
  margin: 0 auto;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.btn-primary {
  background-color: #1e293b;
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background-color: #2f4d84;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}

.btn-secondary {
  background-color: white;
  color: #64748b;
  border: 1px solid #e2e8f0;
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: #f1f5f9;
    color: #1e293b;
  }
}

/* Utils */
.link-primary {
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 500;
  &:hover {
    text-decoration: underline;
  }
}

.text-sm {
  font-size: 0.875rem;
}
.text-xs {
  font-size: 0.75rem;
}

/* Transitions */
.list-enter-active,
.list-leave-active {
  transition: all 0.3s ease;
}
.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
</style>
